'use client';

import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { DataTest } from '@/types/data-test';
import { cva, type VariantProps } from 'class-variance-authority';
import { useMemo } from 'react';

const FieldSet = ({ className, ...props }: React.ComponentProps<'fieldset'>): React.JSX.Element => {
  return (
    <fieldset className={cn(
      'flex flex-col gap-6',
      'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
      className
    )}
    data-slot="field-set"
    {...props}
    />
  );
};

const FieldLegend = ({
  className,
  variant = 'legend',
  ...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label'; }): React.JSX.Element => {
  return (
    <legend className={cn(
      'mb-3 font-medium',
      'data-[variant=legend]:text-base',
      'data-[variant=label]:text-sm',
      className
    )}
    data-slot="field-legend"
    data-variant={variant}
    {...props}
    />
  );
};

const FieldGroup = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  return (
    <div className={cn(
      'group/field-group @container/field-group flex w-full flex-col gap-4',
      'data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4',
      className
    )}
    data-slot="field-group"
    {...props}
    />
  );
};

const fieldVariants = cva(
  'group/field data-[invalid=true]:text-destructive flex w-full gap-1',
  {
    defaultVariants: {
      orientation: 'vertical',
    },
    variants: {
      orientation: {
        horizontal: [
          'flex-row items-center',
          '[&>[data-slot=field-label]]:flex-auto',
          'has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
          'has-[>[data-slot=field-content]]:items-start',
        ],
        responsive: [
          '@md/field-group:flex-row @md/field-group:items-center',
          '@md/field-group:[&>*]:w-auto flex-col [&>*]:w-full [&>.sr-only]:w-auto',
          '@md/field-group:[&>[data-slot=field-label]]:flex-auto',
          '@md/field-group:has-[>[data-slot=field-content]]:items-start',
          '@md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
        ],
        vertical: ['flex-col [&>*]:w-full [&>.sr-only]:w-auto'],
      },
    },
  }
);

const Field = ({
  className,
  dataTest,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants> & {
  dataTest?: string;
}): React.JSX.Element => {
  return (
    <div className={cn('grid gap-0 ring-0 border-0 group', fieldVariants({ orientation }), className)}
      data-orientation={orientation}
      data-slot="form-item"
      data-test={dataTest}
      role="group"
      {...props}
    />
  );
};

const FieldContent = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  return (
    <div className={cn(
      'group/field-content flex flex-1 flex-col gap-1.5 leading-snug',
      className
    )}
    data-slot="field-content"
    {...props}
    />
  );
};

const FieldLabel = ({
  children,
  className,
  dataTest,
  error,
  htmlFor,
  isCheckbox = false,
  required,
  ...props
}: React.ComponentProps<typeof Label> & {
  dataTest?: string;
  error?: boolean;
  htmlFor?: string;
  isCheckbox?: boolean;
  required?: boolean;
}): React.JSX.Element => {
  const showRequired = required && !isCheckbox;

  return (
    <Label className={cn(
      'text-foreground font-bold transition-[color] leading-none',
      !!error && 'data-[checkbox=false]:text-primary group-focus-within:!text-foreground',
      showRequired && '!gap-0',
      className
    )}
    data-checkbox={isCheckbox ? 'true' : 'false'}
    data-error={!!error}
    data-slot="form-label"
    data-test={dataTest}
    htmlFor={htmlFor}
    {...props}
    >
      {showRequired && <span className="text-primary">*</span>}
      {children}
    </Label>
  );
};

const FieldTitle = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  return (
    <div className={cn(
      'flex w-fit items-center gap-2 text-sm font-medium leading-snug',
      'group-data-[disabled=true]/field:opacity-50',
      className
    )}
    data-slot="field-label"
    {...props}
    />
  );
};

const FieldDescription = ({ className, dataTest, ...props }: React.ComponentProps<'p'> & DataTest): React.JSX.Element => {
  return (
    <p className={cn(
      'text-muted-foreground text-sm font-normal leading-normal group-has-[[data-orientation=horizontal]]/field:text-balance',
      '[&:nth-last-child(2)]:-mt-1 last:mt-0 [[data-variant=legend]+&]:-mt-1.5',
      '[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4',
      className
    )}
    data-slot="field-description"
    data-test={dataTest}
    {...props}
    />
  );
};

const FieldSeparator = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  children?: React.ReactNode;
}): React.JSX.Element => {
  return (
    <div className={cn(
      'relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2',
      className
    )}
    data-content={!!children}
    data-slot="field-separator"
    {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children &&
        <span className="bg-background text-muted-foreground relative mx-auto block w-fit px-2"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      }
    </div>
  );
};

const FieldError = ({
  children,
  className,
  errors,
  ...props
}: React.ComponentProps<'div'> & {
  errors?: Array<{ message?: string; } | undefined>;
}): React.JSX.Element | null => {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors) {
      return null;
    }

    if (errors?.length === 1 && errors[0]?.message) {
      return errors[0].message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {errors.map(
          (error, index) =>
            error?.message && <li key={index}>{error.message}</li>
        )}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div className={cn('text-destructive text-sm font-normal', className)}
      data-slot="field-error"
      role="alert"
      {...props}
    >
      {content}
    </div>
  );
};

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
};
